import React from 'react'
import { MdAttachMoney, MdBalance, MdGroups } from 'react-icons/md'

import Container from 'components/Container'
import ContactButton from 'components/ContactButton'
import { classNames } from 'services/string'

export interface FeaturedCardProps {
  title: string
  icon: JSX.Element
  topics: string[]
  color?: string
  redirectToTarget?: boolean
  offerPage?: boolean
}

export const FeaturedCard = ({ title, icon, topics, redirectToTarget, offerPage = false, color }: FeaturedCardProps) => {
  const cardClass = offerPage
    ? 'bg-neutral-900/70 border-[0.5px] border-secondary-600 rounded-2xl flex flex-col h-full'
    : 'bg-white/50 rounded-2xl flex flex-col p-4 h-full gap-4 items-center drop-shadow-2xl shadow-[1px_1px_1px_0px_rgba(255,255,255,1)_inset] text-black opacity-100'

  return (
    <div className={cardClass}>
      {offerPage ? (
        <>
          <div className='text-center space-y-4 px-6 pt-6 pb-4'>
            <div className='mx-auto w-20 h-20 rounded-full bg-neutral-600 flex items-center justify-center transition-all duration-300 group-hover:shadow-[0_0_30px_rgba(234,179,8,0.4)]'>
              <div className={`w-10 h-10 flex items-center justify-center ${color ?? 'text-primary'}`}>{icon}</div>
            </div>
            <h3 className='text-secondary-300 text-2xl font-bold text-foreground'>{title}</h3>
          </div>

          <div className=' px-6'>
            {topics.map((t, i) => (
              <div key={i} className='text-center py-3 border-b border-secondary-600 last:border-0'>
                <p className='text-foreground/90'>{t}</p>
              </div>
            ))}
          </div>

          <div className='mb-4 pt-6 px-6 mt-auto'>
            <ContactButton
              redirectToTarget={redirectToTarget}
              targetId='contact-section'
              className='w-full bg-primary-600 text-black font-semibold'
            />
          </div>
        </>
      ) : (
        <>
          <div className='bg-gradient-to-r from-gray-900 to-gray-600 p-2 rounded-full'>
            <div className='flex justify-center items-center w-16 h-16 rounded-full'>
              <div className={`flex justify-center items-center bg-white/20 rounded-full w-full h-full text-white`}>{icon}</div>
            </div>
          </div>

          <p className='font-semibold lg:font-medium text-md lg:text-xl text-center lg:text-left'>{title}</p>

          <div className='flex flex-col gap-2 w-full'>
            <div className='p-4'>
              {topics.map((topic, index) => (
                <div key={index}>
                  <p className='py-0.5 text-center'>{topic}</p>
                  {index !== topics.length - 1 && <hr className='border-t-[0.5px] border-black/20 my-1' />}
                </div>
              ))}
            </div>
          </div>

          <ContactButton redirectToTarget={redirectToTarget} targetId='contact-section' />
        </>
      )}
    </div>
  )
}

interface FeaturedServicesProps {
  redirectToTarget?: boolean
  offerPage?: boolean
}

const FeaturedServices = ({ redirectToTarget, offerPage }: FeaturedServicesProps) => {
  const featuredCardsContent: FeaturedCardProps[] = [
    {
      title: 'Legalizações',
      icon: <MdBalance size='2.5rem' />,
      topics: ['Abertura de empresa', 'Regularizações', 'Certificados digitais'],
      color: 'text-black',
    },
    {
      title: 'Operações',
      icon: <MdAttachMoney size='2.5rem' />,
      topics: ['Atividades contábeis', 'Atividades fiscais', 'Otimização tributária'],
      color: 'text-black',
    },
    {
      title: 'Recursos Humanos',
      icon: <MdGroups size='2.5rem' />,
      topics: ['Folha de pagamento', 'Admissão e demissão', 'Encargos de folha'],
      color: 'text-black',
    },
  ]

  return (
    <div className={offerPage ? 'bg-black text-white' : undefined}>
      <Container className='pt-8 pb-12 lg:pt-8 lg:pb-16 flex flex-col gap-4'>
        <div className={offerPage ? 'mt-10 text-center lg:w-full' : 'flex flex-col text-center lg:text-left lg:w-2/3'}>
          <h3>
            <span className='font-semibold text-4xl'>
              O que a <span className={classNames('font-semibold text-4xl', offerPage ? 'text-primary-600' : undefined)}>Hubs</span> faz
            </span>
            <br />
            <span className='lg:text-lg'>para ajudar sua empresa?</span>
          </h3>

          <p className={offerPage ? 'mt-8 lg:mt-4 mb-4 max-w-3xl mx-auto' : 'mt-8 lg:mt-4 mb-4'}>
            Fornecemos soluções personalizadas para cada tipo de negócio.
          </p>

          <p className={offerPage ? 'font-light mb-4 max-w-3xl mx-auto' : 'font-light mb-4'}>
            Solicite emissão de notas fiscais, acesse guias e tenha acesso aos documentos de sua empresa de forma simples e eficiente em
            nossa plataforma digital!
          </p>
        </div>

        <div className='relative w-full'>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10 relative z-10'>
            {featuredCardsContent.map((props, index) => (
              <FeaturedCard key={index} {...props} redirectToTarget={redirectToTarget} offerPage={offerPage} />
            ))}
          </div>
        </div>
      </Container>
    </div>
  )
}

export default FeaturedServices
